import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowUpDown,
  Bookmark,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleHelp,
  ClipboardList,
  Download,
  FileDown,
  Lock,
  Package,
  Pin,
  PinOff,
  RefreshCw,
  RotateCcw,
  Settings2,
  SlidersHorizontal,
  Star,
  TrendingDown,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import CoreReplenishmentApi from '../../../services/reports/CoreReplenishment.service';
import { SAVED_VIEW_NAME_MAX_LENGTH, SavedViewVisibility } from '../../../services/reports/SavedViews.service';
import { useSavedViews } from '../../../hooks/useSavedViews';
import { normalizeColumnOrder, useDraggableColumnOrder } from '../../../hooks/useDraggableColumnOrder';
import { STICKY_LAYER, useStickyTableRows } from '../../../hooks/useStickyTableRows';
import { COLUMN_RESIZE_HANDLE_CLASS, useResizableColumns } from '../../../hooks/useResizableColumns';
import { pinColumn, unpinColumn, usePinnedColumns } from '../../../hooks/columnPinning';
import AuthProvider from '../../../providers/AuthProvider';
import { Skeleton } from '../../../tailwindComponents/ui/skeleton';
import { ColumnHeaderMenu, ColumnSortDirection } from '../ColumnHeaderMenu.component';
import { downloadCsv } from '../downloadCsv';
import { toLocalIsoDate } from '../topSellers/TopSellersReport.component';
import { InfoTooltip } from '../agedInventory/InfoTooltip.component';
import { FilterOption, SearchInput } from '../agedInventory/AgedInventoryFilters.component';
import { getSpanDateRangeInfo } from '../vendorPerformance/VendorPerformanceReport.component';
import { CoreReplenishmentFilters, HierarchySelect, toClassOption, usePopover } from './CoreReplenishmentFilters.component';
import { CoreReplenishmentSettings } from './CoreReplenishmentSettings.component';
import {
  CLIENT_SETTING_KEYS,
  COLUMN_GROUPS,
  CORE_REPLENISHMENT_COLUMNS,
  CORE_REPLENISHMENT_STORAGE_PREFIX,
  CoreReplenishmentColumnConfig,
  CoreReplenishmentColumnKey,
  CoreReplenishmentSortKey,
  CoreReplenishmentViewPayload,
  CUSTOM_DATE_RANGE,
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_WIDTHS,
  DEFAULT_CONTROLS,
  DEFAULT_FILTERS,
  DEFAULT_HIERARCHY,
  DEFAULT_SETTINGS,
  DEFAULT_SORT,
  DEFAULT_VISIBLE_COLUMNS,
  EMPTY_CELL,
  INSUFFICIENT_REASON_TEXT,
  METRIC_COLUMN_WIDTH,
  PAGE_SIZE,
  PRODUCT_COLUMN_DESCRIPTION,
  PRODUCT_COLUMN_KEY,
  PRODUCT_COLUMN_LABEL,
  SEARCH_PLACEHOLDER,
  SortConfig,
  VENDOR_TERMS_TOOLTIP,
  buildComputeContext,
  buildCoreReplenishmentCsv,
  buildOrderLines,
  buildOrdersCsv,
  cellText,
  clientSettingsFromRow,
  clientSettingsToRow,
  columnByKey,
  computeProducts,
  computeTotalRow,
  countInsufficientSkus,
  countStatuses,
  filterProducts,
  formatDate,
  getColumnOrderStorageKey,
  getColumnWidthsStorageKey,
  getPinnedColumnsStorageKey,
  groupHasUnitsToOrder,
  groupProducts,
  groupValueOf,
  hierarchyGroupNoun,
  hierarchyHasOtb,
  insufficientSkusText,
  isColumnShown,
  itemCountLabel,
  nextPinState,
  normalizeViewPayload,
  onOrderSubLabel,
  planCoverText,
  planTargetText,
  productCountText,
  productSubLine,
  reorderSubLabel,
  reportFootnote,
  resolveDateRange,
  resolveSettings,
  sortDescriptionParts,
  sortProducts,
  sortTypeOf,
  statusConfig,
  statusCountLabel,
  targetFieldLabel,
  vendorTermsLine,
  viewSettings,
  visibleSkus,
} from './coreReplenishment.logic';
import type {
  ClientSettingsRow,
  CoreReplenishmentApiResponse,
  CoreReplenishmentControls,
  CoreReplenishmentFilterState,
  CoreReplenishmentGroup,
  CoreReplenishmentHierarchy,
  CoreReplenishmentProduct,
  CoreReplenishmentSku,
  ReplenishmentSettings,
  ReplenishmentStatus,
  RowFigures,
  StatusCounts,
} from './coreReplenishment.types';

export interface CoreReplenishmentReportProps {
  clientId: string | number;
  // Accepted for the report registry's common signature.
  epochMonth?: number;
  onClose?: () => void;
}

// subgroup: a vendor row nested under its class.
type RowKind = 'product' | 'sku' | 'group' | 'subgroup' | 'total';
const isAggregateKind = (kind: RowKind): boolean => kind === 'group' || kind === 'subgroup' || kind === 'total';
type DisplayRow = RowFigures & { size_label?: string; color_label?: string };

const ALIGN_CLASS = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;
const JUSTIFY_CLASS = {
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center',
} as const;
const SKELETON_ROW_COUNT = 8;
const SEARCH_DEBOUNCE_MS = 250;
const HEADER_BUTTON_CLASS =
  'h-[34px] px-[14px] bg-white border border-[#D0D5DD] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 transition-colors leading-[21px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';
// The same button pressed in, e.g. Settings while its panel is open.
const HEADER_BUTTON_ACTIVE_CLASS = HEADER_BUTTON_CLASS.replace('bg-white', 'bg-[#F4F0FF]')
  .replace('border-[#D0D5DD]', 'border-[#D6BBFB]')
  .replace('text-[#344054]', 'text-[#6941C6]')
  .replace('hover:bg-[#F9FAFB]', 'hover:bg-[#EBE0FF]');
const ICON_BUTTON_CLASS =
  'h-[34px] w-[34px] bg-white border border-[#D0D5DD] rounded-[9px] text-[#667085] hover:text-[#09090B] hover:bg-[#F9FAFB] shadow-2xs flex items-center justify-center transition-colors cursor-pointer';
const SMALL_BUTTON_CLASS =
  'h-[32px] px-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs cursor-pointer';
const FIRST_COL_EDGE = 'border-r border-[#E4E4E7]';
// border-separate keeps sticky cells' borders attached, but row borders must then sit on the cells.
const ROW_DIVIDER = 'border-b border-[#EAECF0]';

const ROW_BG: Record<RowKind, string> = {
  product: 'bg-white group-hover:bg-[#F9FAFB]',
  sku: 'bg-[#FAFAFB] group-hover:bg-[#F4F4F5]',
  group: 'bg-[#F8F9FA]',
  subgroup: 'bg-[#FCFCFD]',
  total: 'bg-[#F2F4F7]',
};

// First-column indent per tree level (class › vendor › product › SKU).
const BASE_INDENT_PX = 14;
const INDENT_STEP_PX = 18;
const FIRST_COL_RIGHT_PAD_PX = 14;
const indentOf = (depth: number): number => BASE_INDENT_PX + depth * INDENT_STEP_PX;

const HOW_IT_WORKS = [
  'Velocity is net units sold per in-stock day over the velocity lookback. A day counts as in stock when it starts with units on hand or makes a sale.',
  'Stockout Date walks forward day by day from today: on hand, plus open POs as they land, less velocity. POs that land after the stockout do not count; a PO without a date is expected at today + lead time.',
  'Status compares days to stockout with lead time + cadence: Order now within 1.0×, Next order within 1.5×, otherwise Covered. Fewer than the minimum in-stock days, or no receiving feed, is Insufficient data.',
  'Suggested Reorder = 7 × velocity × (lead time + cadence + target weeks), less on hand and POs that land in time. The target is plan cover × core uplift, or your Weeks of supply target.',
  'OTB comes from the class plan for the span, so Category and Total add up their classes. Vendor filters do not change OTB.',
];

const INSUFFICIENT_SORT_NOTE =
  'A SKU has insufficient data when it had fewer in-stock days than the minimum in Report settings, or the client has no receiving feed. Its product has no stockout date or suggestion, and sorts last whatever the sort.';

const toFilterOption = (option: { code: string; name: string }): FilterOption => ({ code: option.code, name: option.name || option.code });

const StatusBadge: React.FC<{
  status: ReplenishmentStatus | null;
  reason?: string | null;
}> = ({ status, reason }) => {
  if (!status) return <span className="text-[#98A2B3]">{EMPTY_CELL}</span>;
  const config = statusConfig(status);
  return (
    <span
      title={reason ?? undefined}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-semibold whitespace-nowrap ${config.badgeClass} ${reason ? 'cursor-help' : ''}`}
    >
      {config.label}
    </span>
  );
};

// Purple marks OTB figures on group and Total rows; a shortfall stays red.
const OTB_COLUMNS: readonly string[] = ['otb', 'otb_after'];

const HeaderButton: React.FC<{
  label: string;
  subLabel?: string;
  description: string;
  isActive: boolean;
  direction: ColumnSortDirection;
  isMenuOpen: boolean;
  align: 'left' | 'right' | 'center';
  tooltipAlign: 'left' | 'right';
  onToggleMenu: () => void;
  menu: React.ReactNode;
}> = ({ label, subLabel, description, isActive, direction, isMenuOpen, align, tooltipAlign, onToggleMenu, menu }) => (
  <div className={`relative flex items-center ${JUSTIFY_CLASS[align]} gap-0.5 w-full`} data-column-menu>
    <InfoTooltip text={description} position="bottom" align={tooltipAlign} disabled={isMenuOpen} />
    <button
      type="button"
      onClick={onToggleMenu}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
      className={`flex items-center gap-1 hover:text-[#09090B] transition-colors cursor-pointer ${ALIGN_CLASS[align]} ${isActive || isMenuOpen ? 'text-[#09090B]' : ''}`}
    >
      <span className="flex flex-col">
        <span className="whitespace-normal break-words leading-tight">{label}</span>
        {subLabel && <span className="text-[11px] font-normal text-[#98A2B3] leading-tight">{subLabel}</span>}
      </span>
      {isActive ? (
        direction === 'asc' ? (
          <ChevronUp className="w-3 h-3 shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 shrink-0" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 text-[#94A3B8] shrink-0" />
      )}
    </button>
    {isMenuOpen && menu}
  </div>
);

export const CoreReplenishmentReport: React.FC<CoreReplenishmentReportProps> = ({ clientId, onClose }) => {
  const auth = AuthProvider.useAuth() as {
    user?: { id?: string | number } | null;
  } | null;
  const userId = auth?.user?.id ?? null;

  // --- Filters, controls, layout ---
  const [filters, setFilters] = useState<CoreReplenishmentFilterState>(DEFAULT_FILTERS);
  const updateFilters = (changes: Partial<CoreReplenishmentFilterState>) => setFilters((prev) => ({ ...prev, ...changes }));
  const [controls, setControls] = useState<CoreReplenishmentControls>(DEFAULT_CONTROLS);
  const updateControls = (changes: Partial<CoreReplenishmentControls>) => setControls((prev) => ({ ...prev, ...changes }));
  const [hierarchy, setHierarchy] = useState<CoreReplenishmentHierarchy>(DEFAULT_HIERARCHY);
  const [sort, setSort] = useState<SortConfig>(DEFAULT_SORT);
  const hasOtb = hierarchyHasOtb(hierarchy);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { from, to } = resolveDateRange(filters.dateRange, filters.customStartDate, filters.customEndDate);
  const popover = usePopover();

  // --- Options ---
  const [options, setOptions] = useState<{
    locations: FilterOption[];
    categories: FilterOption[];
    classes: FilterOption[];
    vendors: FilterOption[];
  }>({
    locations: [],
    categories: [],
    classes: [],
    vendors: [],
  });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      CoreReplenishmentApi.getDropdownOptions(clientId).catch(() => ({
        locations: [],
        categories: [],
        classes: [],
      })),
      CoreReplenishmentApi.getVendorOptions(clientId).catch(() => [] as string[]),
    ]).then(([dropdowns, vendors]) => {
      if (cancelled) return;
      setOptions({
        // Store number before the name, as the location rows show it.
        locations: dropdowns.locations.map((o) => toClassOption(o)),
        categories: dropdowns.categories.map(toFilterOption),
        classes: [...dropdowns.classes].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })).map((o) => toClassOption(o)),
        vendors: vendors.map((vendor) => ({ code: vendor, name: vendor })),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // --- Data ---
  const [response, setResponse] = useState<CoreReplenishmentApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  // Status and core filters, search and every setting apply in the browser, so they don't refetch.
  // Only Product by Location changes the rows; the other hierarchies regroup the same data.
  const byLocation = hierarchy === 'location';
  const requestParams = useMemo(
    () => ({
      clientId,
      from,
      to,
      onOrderSpan: controls.onOrderSpan,
      spanStart: controls.spanStart,
      spanEnd: controls.spanEnd,
      locationCodes: filters.locations,
      excludeLocations: filters.excludeLocations,
      categoryIds: filters.categories,
      excludeCategories: filters.excludeCategories,
      classCodes: filters.classes,
      excludeClasses: filters.excludeClasses,
      vendorIds: filters.vendors,
      excludeVendors: filters.excludeVendors,
      byLocation,
    }),
    [
      clientId,
      from,
      to,
      controls.onOrderSpan,
      controls.spanStart,
      controls.spanEnd,
      filters.locations,
      filters.excludeLocations,
      filters.categories,
      filters.excludeCategories,
      filters.classes,
      filters.excludeClasses,
      filters.vendors,
      filters.excludeVendors,
      byLocation,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    CoreReplenishmentApi.getCoreReplenishment(requestParams)
      .then((res) => {
        if (cancelled) return;
        setResponse(res);
        // A failed query can still answer 200; say so rather than show a quiet empty table.
        if (res?.query_error) setLoadError(res.query_error);
      })
      .catch(() => {
        if (cancelled) return;
        setResponse(null);
        setLoadError('Failed to load Core Item Replenishment data.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestParams, reloadKey]);

  // --- Settings ---
  const [clientRow, setClientRow] = useState<ClientSettingsRow | null>(null);
  useEffect(() => setClientRow(response?.client_settings ?? null), [response]);
  const [userSettings, setUserSettings] = useState<Partial<ReplenishmentSettings>>({});
  const clientOverrides = useMemo(() => clientSettingsFromRow(clientRow), [clientRow]);
  const resolved = useMemo(() => resolveSettings(clientOverrides, userSettings), [clientOverrides, userSettings]);
  const { settings } = resolved;
  const clientSettings = { ...DEFAULT_SETTINGS, ...clientOverrides };
  const hasClientChanges = CLIENT_SETTING_KEYS.some((key) => key in userSettings && userSettings[key] !== clientSettings[key]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [applyingSettings, setApplyingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const handleApplySettings = async () => {
    if (!hasClientChanges) {
      setSettingsOpen(false);
      return;
    }
    setApplyingSettings(true);
    setSettingsError(null);
    try {
      const saved = await CoreReplenishmentApi.saveClientSettings(clientId, clientSettingsToRow(resolved.requested));
      setClientRow(saved);
      setUserSettings((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !CLIENT_SETTING_KEYS.includes(key as never))));
      setSettingsOpen(false);
    } catch (error) {
      console.error('Failed to save core replenishment settings', error);
      setSettingsError('Could not save the client settings. Please try again.');
    } finally {
      setApplyingSettings(false);
    }
  };

  // --- Rows: compute -> filter -> sort -> group -> page ---
  const ctx = useMemo(
    () =>
      response
        ? buildComputeContext(response, {
            settings,
            wosOverride: controls.wosOverride,
            valueBasis: controls.valueBasis,
            from,
            to,
          })
        : null,
    [response, settings, controls.wosOverride, controls.valueBasis, from, to]
  );
  const products = useMemo(() => (response && ctx ? computeProducts(response.rows, ctx) : []), [response, ctx]);
  const sortedProducts = useMemo(
    () =>
      sortProducts(
        filterProducts(products, {
          statuses: filters.statuses,
          coreItems: filters.coreItems,
          hideCovered: controls.hideCovered,
          search: debouncedSearch,
        }),
        sort
      ),
    [products, filters.statuses, filters.coreItems, controls.hideCovered, debouncedSearch, sort]
  );
  const groups = useMemo(() => (ctx ? groupProducts(sortedProducts, hierarchy, ctx) : []), [sortedProducts, hierarchy, ctx]);
  const orderedProducts = useMemo(() => (hierarchy === 'total' ? sortedProducts : groups.flatMap((g) => g.products)), [hierarchy, sortedProducts, groups]);
  const totals = useMemo(() => (ctx ? computeTotalRow(orderedProducts, hierarchy, ctx) : null), [ctx, orderedProducts, hierarchy]);
  const totalCounts = useMemo(() => countStatuses(orderedProducts), [orderedProducts]);
  const skuCount = orderedProducts.reduce((count, p) => count + p.skus.length, 0);
  const vendors = useMemo(() => Array.from(new Set(products.map((p) => p.row.vendor).filter((v): v is string => !!v))).sort(), [products]);
  const orderLines = useMemo(() => buildOrderLines(orderedProducts), [orderedProducts]);
  const sortParts = sortDescriptionParts(sort);
  const insufficientText = insufficientSkusText(useMemo(() => countInsufficientSkus(orderedProducts), [orderedProducts]));
  const planTarget = useMemo(() => (ctx ? planTargetText(orderedProducts, ctx) : null), [orderedProducts, ctx]);
  const planCover = useMemo(() => (ctx ? planCoverText(orderedProducts, ctx.classPlans) : null), [orderedProducts, ctx]);
  const footnote = reportFootnote({
    from,
    to,
    settings,
    wosOverride: controls.wosOverride,
    otbSpan: hasOtb ? getSpanDateRangeInfo(controls.onOrderSpan, new Date(), controls.spanStart, controls.spanEnd).dateRange : null,
  });

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => setVisibleCount(PAGE_SIZE), [response, filters, debouncedSearch, sort, hierarchy, controls.hideCovered]);
  const shownProducts = orderedProducts.slice(0, visibleCount);
  const shownIds = new Set(shownProducts.map((p) => p.id));
  const remaining = orderedProducts.length - shownProducts.length;

  const [collapsedProducts, setCollapsedProducts] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // --- Pin as core ---
  const handleTogglePin = async (p: CoreReplenishmentProduct) => {
    const next = nextPinState(p.detected_core, p.row.pin_state);
    const setPin = (state: typeof next) =>
      setResponse((prev) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((row) => (row.product_id === p.row.product_id ? { ...row, pin_state: state } : row)),
            }
          : prev
      );
    const previous = p.row.pin_state;
    setPin(next);
    setActionError(null);
    try {
      await CoreReplenishmentApi.setPinState(clientId, p.row.product_id, next);
    } catch (error) {
      console.error('Failed to update core pin', error);
      setPin(previous);
      setActionError(`Could not update "${p.row.product_title}". Please try again.`);
    }
  };

  // --- Columns ---
  const [visibleColumns, setVisibleColumns] = useState<CoreReplenishmentColumnKey[]>([...DEFAULT_VISIBLE_COLUMNS]);
  const { columnOrder, updateColumnOrder, resetColumnOrder, getDragProps, dragClass } = useDraggableColumnOrder(
    DEFAULT_COLUMN_ORDER,
    getColumnOrderStorageKey(userId, clientId)
  );
  const { pinnedColumns, isPinned, setPinnedColumns, clearPinnedColumns } = usePinnedColumns(
    getPinnedColumnsStorageKey(userId, clientId),
    DEFAULT_COLUMN_ORDER
  );
  const { widthOf, isResizing, getResizeHandleProps, resetColumnWidths } = useResizableColumns(
    getColumnWidthsStorageKey(userId, clientId),
    DEFAULT_COLUMN_WIDTHS,
    METRIC_COLUMN_WIDTH
  );
  const visibleOrderedColumns: CoreReplenishmentColumnConfig[] = columnOrder
    .map((key) => columnByKey(key))
    .filter((col): col is CoreReplenishmentColumnConfig => col !== undefined && isColumnShown(col, { skuGrain: controls.skuGrain, hasOtb }, visibleColumns));

  const toggleColumnVisible = (key: CoreReplenishmentColumnKey, visible: boolean) =>
    setVisibleColumns((prev) => (visible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));

  const togglePinnedColumn = (key: CoreReplenishmentColumnKey) => {
    if (isPinned(key)) {
      const rest = pinnedColumns.filter((k) => k !== key);
      setPinnedColumns(rest);
      updateColumnOrder(unpinColumn(columnOrder, key, rest, DEFAULT_COLUMN_ORDER));
    } else {
      updateColumnOrder(pinColumn(columnOrder, key, pinnedColumns));
      setPinnedColumns([...pinnedColumns, key]);
    }
  };
  const getColumnDragProps = (key: CoreReplenishmentColumnKey) => (isPinned(key) ? { draggable: false } : getDragProps(key, 'x'));

  const resetColumns = () => {
    setVisibleColumns([...DEFAULT_VISIBLE_COLUMNS]);
    resetColumnOrder();
    clearPinnedColumns();
    resetColumnWidths();
  };

  const [activeMenuColumn, setActiveMenuColumn] = useState<string | null>(null);
  const toggleColumnMenu = (key: string) => setActiveMenuColumn((open) => (open === key ? null : key));
  const closeColumnMenu = () => setActiveMenuColumn(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement | null)?.closest('[data-column-menu]')) setActiveMenuColumn(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Saved views ---
  const {
    views: savedViews,
    saveView,
    deleteView,
  } = useSavedViews<CoreReplenishmentViewPayload>({
    reportKey: 'core_replenishment',
    clientId,
    userId,
    storagePrefix: CORE_REPLENISHMENT_STORAGE_PREFIX,
    readLocalViews: () => [],
  });
  const [newViewName, setNewViewName] = useState('');
  const [newViewVisibility, setNewViewVisibility] = useState<SavedViewVisibility>('only_me');
  const [isSavingView, setIsSavingView] = useState(false);
  const [savedViewError, setSavedViewError] = useState<string | null>(null);

  const handleApplySavedView = (raw: unknown) => {
    const payload = normalizeViewPayload(raw);
    setFilters(payload.filters);
    setControls(payload.controls);
    setHierarchy(payload.hierarchy);
    setSort(payload.sort);
    setUserSettings(payload.settings);
    setVisibleColumns(payload.visibleColumns);
    if (payload.columnOrder.length > 0) updateColumnOrder(normalizeColumnOrder(payload.columnOrder, DEFAULT_COLUMN_ORDER));
    setPinnedColumns(payload.pinnedColumns);
    popover.close();
  };

  const handleSaveCurrentView = async () => {
    const name = newViewName.trim();
    if (!name || isSavingView) return;
    const payload: CoreReplenishmentViewPayload = {
      filters,
      controls,
      hierarchy,
      settings: viewSettings(userSettings),
      sort,
      visibleColumns,
      columnOrder,
      pinnedColumns,
    };
    setIsSavingView(true);
    setSavedViewError(null);
    try {
      await saveView({ name, visibility: newViewVisibility, payload });
      setNewViewName('');
      setNewViewVisibility('only_me');
    } catch (error) {
      console.error('Failed to save core replenishment view', error);
      setSavedViewError('Could not save this view. Please try again.');
    } finally {
      setIsSavingView(false);
    }
  };

  const handleDeleteSavedView = async (id: number, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedViewError(null);
    try {
      await deleteView(id);
    } catch (error) {
      console.error('Failed to delete core replenishment view', error);
      setSavedViewError(`Could not delete "${name}". Please try again.`);
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    updateControls({ hideCovered: false, hideZeroSalesSkus: false });
    setSearchQuery('');
  };

  // --- Exports ---
  const exportDate = toLocalIsoDate(new Date());
  const handleExportCsv = () => {
    const csv = buildCoreReplenishmentCsv(orderedProducts, {
      columns: visibleOrderedColumns,
      basis: controls.valueBasis,
      includeSkus: controls.skuGrain === 'sku',
      hideZeroSalesSkus: controls.hideZeroSalesSkus,
      groupHeader: hierarchyGroupNoun(hierarchy),
      groupOf: (p) => groupValueOf(p, hierarchy),
    });
    downloadCsv(csv, `core-replenishment-${exportDate}-${hierarchy}-${controls.skuGrain}.csv`);
  };
  // A vendor row exports only its own products, so under a class it stays within that class.
  const handleExportOrders = (products?: CoreReplenishmentProduct[], vendor?: string) => {
    const lines = products === undefined ? orderLines : buildOrderLines(products, vendor);
    const suffix = vendor ? `-${vendor.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : '';
    downloadCsv(buildOrdersCsv(lines), `core-replenishment-orders-${exportDate}${suffix}.csv`);
  };

  // --- Sticky rows ---
  const { scrollRef, theadRef, registerGroupBody, updateScrollState, stickyTop, headShadow, footShadow, groupShadow } = useStickyTableRows();

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, orderedProducts, visibleCount, collapsedGroups, collapsedProducts, controls.skuGrain]);

  // --- Cells ---
  const cellStyle = (key: string) => ({
    width: widthOf(key),
    minWidth: widthOf(key),
  });

  const renderValue = (col: CoreReplenishmentColumnConfig, row: DisplayRow, kind: RowKind, counts?: StatusCounts) => {
    const isAggregate = isAggregateKind(kind);
    if (col.key === 'status') {
      if (isAggregate) return <span className="font-medium text-[#344054]">{(counts && statusCountLabel(counts)) ?? EMPTY_CELL}</span>;
      return <StatusBadge status={row.status} reason={row.insufficient_reason ? INSUFFICIENT_REASON_TEXT[row.insufficient_reason] : null} />;
    }
    const text = cellText(row, col.key, isAggregate);
    if (col.key === 'trend_pct' && row.trend_flagged && row.trend_pct !== null) {
      const up = row.trend_pct > 0;
      return (
        <span className={`inline-flex items-center gap-1 font-semibold ${up ? 'text-[#067647]' : 'text-[#B42318]'}`}>
          {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {text}
        </span>
      );
    }
    // Only a product or SKU's own next PO can be late against its own stockout.
    const subLabel =
      col.key === 'on_order_units'
        ? onOrderSubLabel(row, !isAggregate)
        : !isAggregate && col.key === 'suggested_units' && row.status !== 'insufficient_data'
          ? reorderSubLabel(row)
          : null;
    const value = row[col.key as keyof RowFigures];
    const isOtb = OTB_COLUMNS.includes(col.key) && typeof value === 'number';
    const valueClass = isOtb
      ? (value as number) < 0
        ? 'text-[#B42318]'
        : isAggregate
          ? 'text-[#7F56D9]'
          : undefined
      : col.key === 'suggested_dollars' && kind === 'product' && typeof value === 'number' && value > 0
        ? 'font-semibold text-[#101828]'
        : undefined;
    return (
      <span className={`inline-flex flex-col ${col.align === 'right' ? 'items-end' : col.align === 'center' ? 'items-center' : 'items-start'}`}>
        <span className={valueClass}>{text}</span>
        {subLabel && <span className="text-[11px] font-normal text-[#98A2B3]">{subLabel}</span>}
      </span>
    );
  };

  const renderCells = (row: DisplayRow, kind: RowKind, sticky: boolean, counts?: StatusCounts, shadow = '') =>
    visibleOrderedColumns.map((col) => {
      const muted = !isAggregateKind(kind) && row.status === 'insufficient_data' && col.key !== 'status';
      const tone = isAggregateKind(kind)
        ? 'font-semibold text-[#101828]'
        : kind === 'sku'
          ? 'font-medium text-[#64748B]'
          : muted
            ? 'font-medium text-[#98A2B3]'
            : 'font-medium text-[#344054]';
      return (
        <td
          key={col.key}
          className={`px-4 ${kind === 'sku' ? 'py-2 text-[12px]' : 'py-2.5 text-[13px]'} ${tone} ${ALIGN_CLASS[col.align]} whitespace-nowrap ${ROW_BG[kind]} ${ROW_DIVIDER} ${shadow} ${
            sticky ? `sticky ${STICKY_LAYER.pinnedRowCell}` : ''
          }`}
          style={sticky ? { ...cellStyle(col.key), ...stickyTop } : cellStyle(col.key)}
        >
          {renderValue(col, row, kind, counts)}
        </td>
      );
    });

  const renderSkuRows = (p: CoreReplenishmentProduct, depth: number) => {
    if (controls.skuGrain !== 'sku' || collapsedProducts[p.id]) return null;
    return visibleSkus(p, controls.hideZeroSalesSkus).map((sku: CoreReplenishmentSku) => (
      <tr key={sku.id} className="group transition-colors">
        <td
          className={`sticky left-0 z-10 ${FIRST_COL_EDGE} ${ROW_DIVIDER} ${ROW_BG.sku} pr-4 py-2 text-[12px] text-[#64748B] whitespace-nowrap`}
          style={{ paddingLeft: indentOf(depth + 1) + INDENT_STEP_PX }}
        >
          <span className="font-mono">{sku.row.sku_id}</span>
        </td>
        {renderCells(sku, 'sku', false)}
      </tr>
    ));
  };

  const renderProductRows = (p: CoreReplenishmentProduct, depth: number) => {
    const expandable = controls.skuGrain === 'sku' && p.skus.length > 0;
    const isOpen = expandable && !collapsedProducts[p.id];
    const pinTitle = p.is_core ? (p.detected_core ? 'Unpin: not a core item' : 'Unpin core item') : 'Pin as core';
    const indent = indentOf(depth);
    return (
      <React.Fragment key={p.id}>
        <tr className="group transition-colors" data-testid="core-replenishment-product-row">
          <td className={`sticky left-0 z-10 ${FIRST_COL_EDGE} ${ROW_DIVIDER} ${ROW_BG.product} pr-3.5 py-2`} style={{ paddingLeft: indent }}>
            <div
              className="flex items-start gap-1.5 min-w-0"
              style={{
                width: `calc(${widthOf(PRODUCT_COLUMN_KEY)}px - ${indent + FIRST_COL_RIGHT_PAD_PX}px)`,
              }}
            >
              {expandable && (
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedProducts((prev) => ({
                      ...prev,
                      [p.id]: !prev[p.id],
                    }))
                  }
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${p.row.product_title}`}
                  className="mt-0.5 p-0.5 text-[#98A2B3] hover:text-[#7F56D9] shrink-0 cursor-pointer"
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-[#7F56D9]" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate text-[#101828] text-[13px] font-semibold leading-[20px]" title={p.row.product_title}>
                    {p.row.product_title}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleTogglePin(p)}
                    title={pinTitle}
                    aria-label={`${pinTitle}: ${p.row.product_title}`}
                    aria-pressed={p.row.pin_state === 'pinned'}
                    className={`shrink-0 p-0.5 rounded cursor-pointer transition-colors ${
                      p.row.pin_state === 'pinned'
                        ? 'text-[#7F56D9]'
                        : p.row.pin_state === 'unpinned'
                          ? 'text-[#B42318]'
                          : 'text-[#98A2B3] hover:text-[#7F56D9]'
                    }`}
                  >
                    {p.row.pin_state === 'unpinned' ? (
                      <PinOff className="w-3.5 h-3.5" />
                    ) : (
                      <Pin className={`w-3.5 h-3.5 ${p.row.pin_state === 'pinned' ? 'fill-current' : ''}`} />
                    )}
                  </button>
                </div>
                <span className="text-[12px] text-[#667085] leading-[16px] truncate">{productSubLine(p)}</span>
              </div>
            </div>
          </td>
          {renderCells(p, 'product', false)}
        </tr>
        {isOpen && renderSkuRows(p, depth)}
      </React.Fragment>
    );
  };

  // A class, category, vendor or location row. Top-level rows stick under the header; nested
  // vendor rows (Class › Vendor › Product) scroll with their products.
  const renderGroupRow = (group: CoreReplenishmentGroup, depth: number, isOpen: boolean) => {
    const sticky = depth === 0;
    const shadow = sticky ? groupShadow(group.key) : '';
    const kind: RowKind = sticky ? 'group' : 'subgroup';
    return (
      <tr key={`row-${group.key}`} data-testid={sticky ? 'core-replenishment-group-row' : 'core-replenishment-subgroup-row'}>
        <td
          className={`sticky left-0 ${sticky ? STICKY_LAYER.pinnedRowFirstColumn : STICKY_LAYER.firstColumn} ${FIRST_COL_EDGE} ${ROW_DIVIDER} ${shadow} ${ROW_BG[kind]} pr-3.5 py-2`}
          style={sticky ? { ...stickyTop, paddingLeft: indentOf(depth) } : { paddingLeft: indentOf(depth) }}
        >
          <div className="flex items-start gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.key]: isOpen }))}
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${group.label}`}
              className="mt-0.5 shrink-0 cursor-pointer"
            >
              {isOpen ? <ChevronDown className="w-4 h-4 text-[#344054]" /> : <ChevronRight className="w-4 h-4 text-[#344054]" />}
            </button>
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                {group.code && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-white border border-[#D0D5DD] text-[11px] font-bold text-[#344054]">{group.code}</span>
                )}
                <span className="text-[13px] font-bold text-[#101828] truncate" title={group.label}>
                  {group.label}
                </span>
                <span className="shrink-0 text-[12px] font-normal text-[#667085]">{itemCountLabel(group.products.length)}</span>
                {group.vendor !== null && groupHasUnitsToOrder(group) && (
                  <button
                    type="button"
                    onClick={() => handleExportOrders(group.products, group.vendor as string)}
                    title={`Export order for ${group.label}`}
                    aria-label={`Export order for ${group.label}`}
                    className="shrink-0 p-0.5 rounded text-[#7F56D9] hover:text-[#53389E] hover:bg-[#F4F0FF] cursor-pointer"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {group.terms && (
                <span className="text-[12px] font-normal text-[#667085] truncate" title={VENDOR_TERMS_TOOLTIP}>
                  {vendorTermsLine(group.terms)}
                </span>
              )}
            </div>
          </div>
        </td>
        {renderCells(group.figures, kind, sticky, group.statusCounts, shadow)}
      </tr>
    );
  };

  // Rows for a group and, while it is open, its vendor rows or products.
  const renderGroupContent = (group: CoreReplenishmentGroup, depth: number): React.ReactNode[] => {
    const shown = group.products.filter((p) => shownIds.has(p.id));
    if (shown.length === 0) return [];
    const isOpen = !collapsedGroups[group.key];
    const rows: React.ReactNode[] = [renderGroupRow(group, depth, isOpen)];
    if (!isOpen) return rows;
    if (group.children.length > 0) return rows.concat(group.children.flatMap((child) => renderGroupContent(child, depth + 1)));
    return rows.concat(shown.map((p) => renderProductRows(p, depth + 1)));
  };

  const renderGroup = (group: CoreReplenishmentGroup) => {
    const rows = renderGroupContent(group, 0);
    if (rows.length === 0) return null;
    return (
      <tbody key={`group-${group.key}`} ref={registerGroupBody(group.key)}>
        {rows}
      </tbody>
    );
  };

  const colSpan = visibleOrderedColumns.length + 1;
  const hasActiveFilters =
    JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS) || controls.hideCovered || controls.hideZeroSalesSkus || debouncedSearch !== '';
  const exportDisabled = loading || !!loadError || orderedProducts.length === 0;

  const renderBodyState = () => {
    if (loading) {
      return (
        <tbody>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <tr key={`skeleton-${i}`} data-testid="core-replenishment-skeleton-row">
              <td className={`sticky left-0 z-10 bg-white ${FIRST_COL_EDGE} ${ROW_DIVIDER} px-3.5 py-3`}>
                <Skeleton className="h-4 w-3/4 bg-[#F2F4F7]" />
              </td>
              {visibleOrderedColumns.map((col) => (
                <td key={col.key} className={`px-4 py-3 ${ROW_DIVIDER}`}>
                  <Skeleton className="h-4 w-full bg-[#F2F4F7]" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      );
    }
    if (loadError && orderedProducts.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={colSpan} className="px-4 py-14 text-center">
              <div className="flex flex-col items-center gap-2" role="alert">
                <div className="w-10 h-10 rounded-full bg-[#FEF3F2] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#B42318]" />
                </div>
                <p className="text-[14px] font-semibold text-[#101828]">Something went wrong loading this report</p>
                <p className="text-[13px] text-[#71717A]">{loadError}</p>
                <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={`mt-1 ${SMALL_BUTTON_CLASS}`}>
                  Try again
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }
    if (orderedProducts.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={colSpan} className="px-4 py-14 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#F2F4F7] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#98A2B3]" />
                </div>
                <p className="text-[14px] font-semibold text-[#101828]">{hasActiveFilters ? 'No core items match these filters' : 'No core items found'}</p>
                <p className="text-[13px] text-[#71717A]">
                  {hasActiveFilters
                    ? 'Try removing a filter or widening Core items.'
                    : 'Items show here once they sell in enough of the recent weeks, or are pinned as core.'}
                </p>
                {hasActiveFilters && (
                  <button type="button" onClick={clearFilters} className={`mt-1 ${SMALL_BUTTON_CLASS}`}>
                    Clear filters
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      );
    }
    if (hierarchy === 'total') return <tbody>{shownProducts.map((p) => renderProductRows(p, 0))}</tbody>;
    return <>{groups.map(renderGroup)}</>;
  };

  return (
    <div className="w-full bg-[#FAFAFC] min-h-screen text-[#09090B] font-sans antialiased relative flex items-start">
      {/* The settings panel docks flush right, beside the whole report rather than just the table. */}
      <div className="flex-1 min-w-0 p-6 space-y-4">
        {/* --- Breadcrumb & header --- */}
        <div className="flex flex-col gap-3">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-[#667085]">
            {onClose ? (
              <button type="button" onClick={onClose} className="hover:text-[#344054] cursor-pointer">
                Reports
              </button>
            ) : (
              <span>Reports</span>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-[#98A2B3]" />
            <span>Vendor &amp; SKU</span>
            <ChevronRight className="w-3.5 h-3.5 text-[#98A2B3]" />
            <span aria-current="page" className="font-medium text-[#101828]">
              Core Item Replenishment
            </span>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                disabled={loading}
                title="Refresh data"
                aria-label="Refresh data"
                className="w-9 h-9 rounded-full bg-[#F5F0FF] border border-[#E9D7FE] flex items-center justify-center shadow-2xs shrink-0 text-[#7C3AED] hover:bg-[#EBE0FF] transition-colors cursor-pointer disabled:cursor-wait"
              >
                <RefreshCw className={`w-[18px] h-[18px] ${loading ? 'animate-spin' : ''}`} />
              </button>
              <div>
                <h1 className="text-[22px] font-bold text-[#09090B] tracking-tight leading-tight">Core Item Replenishment</h1>
                <p className="text-[13px] text-[#71717A] mt-1 max-w-xl leading-normal">
                  Which core items run out before your next order lands, and how many units to order by vendor. Suggested reorder is in units only.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative" data-popover-container>
                <button
                  type="button"
                  onClick={() => popover.toggle('how_it_works')}
                  aria-expanded={popover.isOpen('how_it_works')}
                  className="h-[34px] px-2.5 rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F2F4F7] flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <CircleHelp className="w-4 h-4 text-[#667085]" />
                  <span>How this report works</span>
                </button>
                {popover.isOpen('how_it_works') && (
                  <div
                    role="dialog"
                    aria-label="How this report works"
                    className="absolute right-0 top-11 z-[60] w-[420px] bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-2.5 text-[13px] text-[#475467] leading-relaxed"
                  >
                    <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                      <h4 className="text-[13px] font-bold text-[#101828]">How this report works</h4>
                      <button type="button" onClick={popover.close} aria-label="Close" className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {HOW_IT_WORKS.map((text) => (
                      <p key={text}>{text}</p>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setSettingsOpen((open) => !open)}
                aria-pressed={settingsOpen}
                className={settingsOpen ? HEADER_BUTTON_ACTIVE_CLASS : HEADER_BUTTON_CLASS}
              >
                <Settings2 className={`w-4 h-4 ${settingsOpen ? 'text-[#7F56D9]' : 'text-[#667085]'}`} />
                <span>Settings</span>
              </button>

              <div className="relative" data-popover-container>
                <button type="button" onClick={() => popover.toggle('saved_views')} aria-expanded={popover.isOpen('saved_views')} className={HEADER_BUTTON_CLASS}>
                  <Bookmark className="w-4 h-4 text-[#667085]" />
                  <span>Save view</span>
                </button>
                {popover.isOpen('saved_views') && (
                  <div className="absolute right-0 top-11 z-[60] w-72 bg-white rounded-2xl shadow-xl border border-[#E4E4E7] p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-[#EAECF0] pb-2">
                      <h4 className="text-[13px] font-bold text-[#101828]">Saved Views</h4>
                      <button type="button" onClick={popover.close} aria-label="Close saved views" className="text-[#98A2B3] hover:text-[#344054] cursor-pointer">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {savedViews.length === 0 && <p className="text-[12px] text-[#98A2B3] italic px-1">No saved views yet.</p>}
                      {savedViews.map((sv) => (
                        <div
                          key={sv.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleApplySavedView(sv.payload)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleApplySavedView(sv.payload);
                          }}
                          className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-[#F9FAFB] cursor-pointer text-[13px] font-semibold text-[#344054]"
                        >
                          <span className="flex flex-col min-w-0">
                            <span className="truncate">{sv.name}</span>
                            {sv.visibility === 'shared' && (
                              <span className="text-[11px] font-medium text-[#667085] flex items-center gap-1 truncate">
                                <Users className="w-3 h-3 shrink-0" />
                                {sv.canEdit || !sv.ownerName ? 'Shared' : `Shared by ${sv.ownerName}`}
                              </span>
                            )}
                          </span>
                          {sv.canEdit && (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSavedView(sv.id, sv.name, e)}
                              aria-label={`Delete saved view ${sv.name}`}
                              className="text-[#98A2B3] hover:text-[#B42318] cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-[#EAECF0] pt-3 space-y-2">
                      <p className="text-[11px] text-[#667085]">Keeps filters, hierarchy, weeks of supply target, settings, sort, columns and pins.</p>
                      <input
                        type="text"
                        placeholder="New view name..."
                        aria-label="New view name"
                        value={newViewName}
                        maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                        onChange={(e) => setNewViewName(e.target.value)}
                        className="w-full px-3 py-1.5 text-[13px] border border-[#D0D5DD] rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] text-[#344054]"
                      />
                      <div className="flex items-center gap-2" role="group" aria-label="Visibility">
                        {(
                          [
                            ['only_me', 'Only me', Lock],
                            ['shared', 'Shared', Users],
                          ] as const
                        ).map(([value, label, Icon]) => (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={newViewVisibility === value}
                            onClick={() => setNewViewVisibility(value)}
                            className={`flex-1 py-1.5 rounded-[9px] text-[12px] font-semibold flex items-center justify-center gap-1.5 cursor-pointer ${
                              newViewVisibility === value
                                ? 'bg-[#F4EBFF] text-[#6941C6] border border-[#D6BBFB]'
                                : 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" /> {label}
                          </button>
                        ))}
                      </div>
                      {savedViewError && (
                        <p role="alert" className="text-[12px] text-[#B42318]">
                          {savedViewError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveCurrentView}
                        disabled={isSavingView || !newViewName.trim()}
                        className="w-full py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-semibold rounded-[9px] shadow-xs cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Save Current Config
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button type="button" onClick={handleExportCsv} disabled={exportDisabled} className={HEADER_BUTTON_CLASS}>
                <Download className="w-4 h-4 text-[#667085]" />
                <span>Export CSV</span>
              </button>
              <button type="button" onClick={() => handleExportOrders()} disabled={exportDisabled || orderLines.length === 0} className={HEADER_BUTTON_CLASS}>
                <ClipboardList className="w-4 h-4 text-[#667085]" />
                <span>Export orders</span>
              </button>
              <button type="button" onClick={() => popover.toggle('saved_views')} className={ICON_BUTTON_CLASS} title="Saved views" aria-label="Saved views">
                <Star className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* --- Control card --- */}
        <CoreReplenishmentFilters
          filters={filters}
          onFiltersChange={updateFilters}
          controls={controls}
          onControlsChange={updateControls}
          options={options}
          hasOtb={hasOtb}
          planTargetLabel={targetFieldLabel(null, settings)}
          planTargetValue={planTarget}
          dateRangeLabel={filters.dateRange === CUSTOM_DATE_RANGE ? `${formatDate(from)} – ${formatDate(to)}` : filters.dateRange}
          onClearFilters={clearFilters}
        />

        {/* --- Below the card: hierarchy and search tools --- */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <HierarchySelect
            value={hierarchy}
            isOpen={popover.isOpen('hierarchy')}
            onToggle={() => popover.toggle('hierarchy')}
            onChange={(value) => {
              setHierarchy(value);
              popover.close();
            }}
          />
          <div className="flex items-end gap-2">
            <SearchInput value={searchQuery} placeholder={SEARCH_PLACEHOLDER} onChange={setSearchQuery} />
            <div className="relative" data-popover-container>
              <button
                type="button"
                onClick={() => popover.toggle('columns')}
                aria-expanded={popover.isOpen('columns')}
                className="h-[34px] px-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-2xs flex items-center gap-1.5 cursor-pointer"
              >
                <SlidersHorizontal className="w-4 h-4 text-[#667085]" />
                <span>Columns</span>
              </button>
              {popover.isOpen('columns') && (
                <div
                  role="dialog"
                  aria-label="Visible columns"
                  className="absolute right-0 top-full mt-1.5 z-50 w-72 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3.5 flex flex-col gap-1"
                >
                  <div className="max-h-[60vh] overflow-y-auto pr-1 flex flex-col gap-2">
                    <label className="flex items-center gap-2.5 p-1 text-[13px] font-semibold text-[#98A2B3]">
                      <input type="checkbox" checked disabled className="w-4 h-4 accent-[#7F56D9]" />
                      <span>{PRODUCT_COLUMN_LABEL}</span>
                      <Lock className="w-3 h-3 ml-auto" />
                    </label>
                    {COLUMN_GROUPS.map((group) => (
                      <div key={group.key} className="flex flex-col gap-0.5">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#71717A] px-1 pt-1">{group.label}</div>
                        {CORE_REPLENISHMENT_COLUMNS.filter((col) => col.group === group.key).map((col) => {
                          const unavailable = (col.otbOnly && !hasOtb) || (col.skuOnly && controls.skuGrain !== 'sku');
                          return (
                            <label key={col.key} className="flex items-start gap-2.5 cursor-pointer hover:bg-[#F9FAFB] p-1 rounded-lg">
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(col.key)}
                                onChange={(e) => toggleColumnVisible(col.key, e.target.checked)}
                                className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
                              />
                              <span className="flex flex-col min-w-0">
                                <span className={`text-[13px] font-semibold leading-[20px] ${unavailable ? 'text-[#98A2B3]' : 'text-[#344054]'}`}>
                                  {col.label}
                                </span>
                                {unavailable && (
                                  <span className="text-[12px] text-[#71717A] leading-[16px]">
                                    {col.otbOnly && !hasOtb ? 'Open-to-buy hierarchies only' : 'SKU view only'}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <p className="border-t border-[#EAECF0] mt-1.5 pt-2 px-1 text-[12px] text-[#71717A]">Drag column headers to reorder them in the table.</p>
                </div>
              )}
            </div>
            <button type="button" onClick={resetColumns} title="Reset columns" aria-label="Reset columns" className={ICON_BUTTON_CLASS}>
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* --- Row count --- */}
        <div className="flex flex-wrap items-center gap-x-1 text-[13px] text-[#71717A] px-1" aria-live="polite" data-testid="core-replenishment-row-count">
          {loading ? (
            <span>Loading&hellip;</span>
          ) : (
            <>
              <span>
                {productCountText(orderedProducts.length, products.length, skuCount)} · sorted by{' '}
                <strong className="font-semibold text-[#344054]">{sortParts.label}</strong> ({sortParts.wording})
                {sortParts.thenLabel && (
                  <>
                    , then <strong className="font-semibold text-[#344054]">{sortParts.thenLabel}</strong>
                  </>
                )}
                {insufficientText && ` · ${insufficientText}`}
              </span>
              {insufficientText && <InfoTooltip text={INSUFFICIENT_SORT_NOTE} position="bottom" align="left" />}
            </>
          )}
        </div>

        {(actionError || (loadError && orderedProducts.length > 0)) && (
          <div role="alert" className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{actionError ?? loadError}</span>
          </div>
        )}

        {/* --- Table --- */}
        <div className="flex flex-col gap-3">
          <div className="bg-white border border-[#E4E4E7] rounded-2xl shadow-2xs overflow-hidden relative z-10">
            <div ref={scrollRef} onScroll={updateScrollState} className="overflow-auto max-h-[calc(100vh-220px)]">
              <table className="w-full text-left text-[13px] border-separate border-spacing-0">
                <thead ref={theadRef} className={`sticky top-0 ${STICKY_LAYER.header}`}>
                  <tr className="text-[#64748B] font-semibold select-none">
                    <th
                      style={cellStyle(PRODUCT_COLUMN_KEY)}
                      className={`sticky left-0 top-0 bg-[#F9F9FB] ${activeMenuColumn === PRODUCT_COLUMN_KEY ? STICKY_LAYER.menuOpen : STICKY_LAYER.headerFirstColumn} px-3.5 py-2.5 ${FIRST_COL_EDGE} border-b border-[#E4E4E7] text-left align-bottom ${headShadow}`}
                    >
                      <HeaderButton
                        label={PRODUCT_COLUMN_LABEL}
                        description={PRODUCT_COLUMN_DESCRIPTION}
                        isActive={sort.key === PRODUCT_COLUMN_KEY}
                        direction={sort.direction}
                        isMenuOpen={activeMenuColumn === PRODUCT_COLUMN_KEY}
                        align="left"
                        tooltipAlign="left"
                        onToggleMenu={() => toggleColumnMenu(PRODUCT_COLUMN_KEY)}
                        menu={
                          <ColumnHeaderMenu
                            title={PRODUCT_COLUMN_LABEL}
                            description={PRODUCT_COLUMN_DESCRIPTION}
                            sortType="text"
                            activeDirection={sort.key === PRODUCT_COLUMN_KEY ? sort.direction : null}
                            onSort={(direction) => setSort({ key: PRODUCT_COLUMN_KEY, direction })}
                            onClose={closeColumnMenu}
                          />
                        }
                      />
                      <span {...getResizeHandleProps(PRODUCT_COLUMN_KEY, PRODUCT_COLUMN_LABEL)} className={COLUMN_RESIZE_HANDLE_CLASS} />
                    </th>
                    {visibleOrderedColumns.map((col, idx) => {
                      const isMenuOpen = activeMenuColumn === col.key;
                      const nearRightEdge = idx >= visibleOrderedColumns.length - 2;
                      const pinned = isPinned(col.key);
                      const sortKey: CoreReplenishmentSortKey = col.key;
                      return (
                        <th
                          key={col.key}
                          {...getColumnDragProps(col.key)}
                          draggable={!isResizing && !pinned && !isMenuOpen}
                          style={cellStyle(col.key)}
                          className={`relative px-4 py-2.5 sticky top-0 bg-[#F9F9FB] border-b border-[#E4E4E7] align-bottom ${headShadow} ${isMenuOpen ? STICKY_LAYER.menuOpen : STICKY_LAYER.header} hover:z-50 ${
                            ALIGN_CLASS[col.align]
                          } ${pinned ? '' : 'cursor-grab'} ${dragClass(col.key, 'x')}`}
                        >
                          <HeaderButton
                            label={col.label}
                            subLabel={col.headerSubLabel ?? (col.valueBasis ? (controls.valueBasis === 'cost' ? 'Cost' : 'Retail') : undefined)}
                            description={col.description}
                            isActive={sort.key === sortKey}
                            direction={sort.direction}
                            isMenuOpen={isMenuOpen}
                            align={col.align}
                            tooltipAlign={nearRightEdge ? 'right' : 'left'}
                            onToggleMenu={() => toggleColumnMenu(col.key)}
                            menu={
                              <ColumnHeaderMenu
                                title={col.label}
                                description={col.description}
                                sortType={sortTypeOf(col.key)}
                                activeDirection={sort.key === sortKey ? sort.direction : null}
                                onSort={(direction) => setSort({ key: sortKey, direction })}
                                pin={{
                                  isPinned: pinned,
                                  onToggle: () => togglePinnedColumn(col.key),
                                }}
                                onHide={() => toggleColumnVisible(col.key, false)}
                                onClose={closeColumnMenu}
                                align={nearRightEdge ? 'right' : 'left'}
                              />
                            }
                          />
                          <span {...getResizeHandleProps(col.key, col.label)} className={COLUMN_RESIZE_HANDLE_CLASS} />
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                {renderBodyState()}

                {!loading && totals && orderedProducts.length > 0 && (
                  <tfoot className={`sticky bottom-0 ${STICKY_LAYER.footer}`}>
                    <tr data-testid="core-replenishment-total-row">
                      <td className={`sticky left-0 ${STICKY_LAYER.footerFirstColumn} ${FIRST_COL_EDGE} border-t border-[#E4E4E7] ${footShadow} ${ROW_BG.total} px-3.5 py-2.5`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-bold text-[#101828]">Total</span>
                          <span className="text-[12px] text-[#667085]">{itemCountLabel(orderedProducts.length)}</span>
                        </div>
                      </td>
                      {visibleOrderedColumns.map((col) => (
                        <td
                          key={col.key}
                          className={`px-4 py-2.5 text-[13px] font-semibold text-[#101828] border-t border-[#E4E4E7] ${footShadow} ${ROW_BG.total} ${ALIGN_CLASS[col.align]} whitespace-nowrap`}
                          style={cellStyle(col.key)}
                        >
                          {renderValue(col, totals, 'total', totalCounts)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {!loading && remaining > 0 && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#E4E4E7] text-[13px] text-[#667085]">
                <span>
                  Showing {shownProducts.length} of {orderedProducts.length} products
                </span>
                <button type="button" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} className={SMALL_BUTTON_CLASS}>
                  Show {Math.min(PAGE_SIZE, remaining)} more
                </button>
              </div>
            )}
          </div>

          {!loading && (
            <p className="flex items-start gap-1.5 px-1 text-[12px] leading-[18px] text-[#71717A]" data-testid="core-replenishment-footnote">
              <CircleHelp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#98A2B3]" aria-hidden="true" />
              <span>{footnote}</span>
            </p>
          )}
        </div>
      </div>

      <CoreReplenishmentSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        resolved={resolved}
        wosOverride={controls.wosOverride}
        planCover={planCover}
        planTarget={planTarget}
        vendors={vendors}
        onChange={(changes) => setUserSettings((prev) => ({ ...prev, ...changes }))}
        onResetToClientDefaults={() => setUserSettings({})}
        onSaveToView={() => popover.open('saved_views')}
        onApply={handleApplySettings}
        hasClientChanges={hasClientChanges}
        applying={applyingSettings}
        applyError={settingsError}
      />
    </div>
  );
};

export default CoreReplenishmentReport;
